import type { Row } from "@tanstack/react-table"
import type { FC } from "react"

import { useCostBreakdownColumns, useCostBreakdownTable } from "../../hooks"

import { CostBreakdownExpandedContent } from "./CostBreakdownExpandedContent"

import { BaseGenericTable } from "@/components/Tables/BaseGenericTable"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { getGoodsReceiptRoutePathFactory } from "@/screens/WarehouseReceiving/utils"

interface CostBreakdownTableProps {
  onCostEdit: (goodsReceipt: GoodsReceipt) => void
  onProductCostEdit?: (goodsReceipt: GoodsReceipt, purchaseOrderLineId: string) => void
}

export const CostBreakdownTable: FC<CostBreakdownTableProps> = ({ onCostEdit, onProductCostEdit }) => {
  const { isTabletView } = useAppView()
  const {
    goodsReceipts,
    isGoodsReceiptsFetching,
    expandedState,
    sortingState,
    tableContainerRef,
    handleExpandedChange,
    handleSortingChange,
    handleScroll,
  } = useCostBreakdownTable()

  const columns = useCostBreakdownColumns({
    status: GoodsReceiptStatus.CheckedIn,
    onEdit: onCostEdit,
  })

  const renderExpandedContent = (row: Row<GoodsReceipt>) => (
    <CostBreakdownExpandedContent row={row} onCostEdit={onCostEdit} onProductCostEdit={onProductCostEdit} />
  )

  return (
    <BaseGenericTable
      data={goodsReceipts}
      isLoading={isGoodsReceiptsFetching}
      expandedState={expandedState}
      sortingState={sortingState}
      tableContainerRef={tableContainerRef}
      getRowId={(row: GoodsReceipt) => row.goodsReceiptId}
      onExpandedChange={handleExpandedChange}
      onSortingChange={handleSortingChange}
      onScroll={handleScroll}
      renderExpandedContent={renderExpandedContent}
      columns={columns}
      enableRowClick={false}
      noDataMessage='No cost breakdown items found'
      getRoutePath={getGoodsReceiptRoutePathFactory(GoodsReceiptStatus.CheckedIn, isTabletView)}
    />
  )
}
